import { Contact } from "../interfaces/DTO/crm/contact";
import { DataAccessTypeEnum } from "../interfaces/DTO/features/ShareContact";

// ─── צבעי Avatar לפי סוג לקוח / מצב ────────────────────────────────────────

export const AVATAR_COLOR_REV_CLIENT          = "#6D4C41"; // חום - לקוח REV&U
export const AVATAR_COLOR_REV_CLIENT_UNDER_21 = "#A1887F"; // חום בהיר - לקוח REV&U מתחת לגיל 21
export const AVATAR_COLOR_SHARED              = "#9c27b0"; // סגול - לקוח משותף
export const AVATAR_COLOR_DEFAULT_CONTACT     = "#2196F3"; // כחול - ברירת מחדל
export const AVATAR_COLOR_UNDER_21            = "#64B5F6"; // כחול בהיר - לקוח מתחת לגיל 21
export const AVATAR_COLOR_STALE_DATA          = "#9E9E9E"; // אפור - מידע לא עדכני (מעל 3 חודשים)
export const AVATAR_COLOR_PRE_ADVICE_RECENT   = "#2196F3"; // כחול - טרום יעוץ עדכני (עד 5 חודשים)
export const AVATAR_COLOR_PRE_ADVICE_OLD      = "#9E9E9E"; // אפור - טרום יעוץ לא עדכני (מעל חודש)

const SHARED_ACCESS_TYPES = [
  DataAccessTypeEnum.SHARED_IN,
  DataAccessTypeEnum.SHARED_IN_ANON,
  DataAccessTypeEnum.DEMO,
];

// ─────────────────────────────────────────────────────────────────────────────

export interface AvatarStyleConfig {
  backgroundColor: string;
  color?: string;
  border?: string;
}

/**
 * חישוב סגנון Avatar לפי תקופת דיווח נתונים, גיל, שיתוף ו-REV&U.
 * כל המידע נגזר ישירות מה-contact.
 * @param solidColor - אם true, מחזיר צבע מלא בלבד ללא מימד זמן או טרום ייעוץ
 */
export function calculateAvatarStyle(contact: Contact, solidColor = false): AvatarStyleConfig {
  const isRev = contact.rev_client === true;
  const isShared = SHARED_ACCESS_TYPES.includes(contact.data_access_type as DataAccessTypeEnum);

  const baseColor = isShared
    ? AVATAR_COLOR_SHARED
    : isRev
    ? AVATAR_COLOR_REV_CLIENT
    : AVATAR_COLOR_DEFAULT_CONTACT;

  // צבע מלא בלי מימד זמן / טרום ייעוץ
  if (solidColor) {
    return { backgroundColor: baseColor, color: "#FFFFFF" };
  }

  const today = new Date();
  const threeMonthsAgo = new Date(today);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

  const age = contact.date_of_birth ? calculateAge(contact.date_of_birth) : null;

  // מתחת ל-21: REV → חום בהיר, אחר → כחול בהיר
  if (age !== null && age < 21) {
    return {
      backgroundColor: isRev ? AVATAR_COLOR_REV_CLIENT_UNDER_21 : AVATAR_COLOR_UNDER_21,
      color: "#FFFFFF",
    };
  }

  const hasLastDataReportedPeriod = !!contact.last_data_reported_period;
  const hasLastPreAdviceDataPeriod = !!contact.last_pre_advice_data_period;

  // יש last_data_reported_period
  if (hasLastDataReportedPeriod) {
    const lastReportedDate = parsePeriodToDate(contact.last_data_reported_period!);
    if (lastReportedDate && lastReportedDate < threeMonthsAgo) {
      return { backgroundColor: AVATAR_COLOR_STALE_DATA, color: "#FFFFFF" };
    }
    return { backgroundColor: baseColor, color: "#FFFFFF" };
  }

  // יש רק last_pre_advice_data_period
  if (hasLastPreAdviceDataPeriod) {
    const lastPreAdviceDate = parsePeriodToDate(contact.last_pre_advice_data_period!);
    if (lastPreAdviceDate) {
      const monthsDiff = getMonthsDifference(lastPreAdviceDate, today);

      if (monthsDiff <= 5) {
        const borderColor = isRev ? AVATAR_COLOR_REV_CLIENT : AVATAR_COLOR_PRE_ADVICE_RECENT;
        return { backgroundColor: "transparent", color: borderColor, border: `2px solid ${borderColor}` };
      } else {
        return { backgroundColor: "transparent", color: AVATAR_COLOR_PRE_ADVICE_OLD, border: `2px solid ${AVATAR_COLOR_PRE_ADVICE_OLD}` };
      }
    }
  }

  // אין שום תקופת דיווח - מסגרת בלבד בצבע הבסיסי
  return {
    backgroundColor: "transparent",
    color: baseColor,
    border: `2px solid ${baseColor}`,
  };
}

function calculateAge(dateOfBirth: string): number {
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

function parsePeriodToDate(period: string): Date | null {
  try {
    const [year, month] = period.split("-").map(Number);
    if (!year || !month) return null;
    return new Date(year, month - 1, 1);
  } catch {
    return null;
  }
}

function getMonthsDifference(date1: Date, date2: Date): number {
  const months = (date2.getFullYear() - date1.getFullYear()) * 12;
  return months + date2.getMonth() - date1.getMonth();
}
